import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import { UploadFile } from '@mui/icons-material';
import { ImportEvent, ImportSummary } from '@/features/swiftFlowErrors/importSwiftFlowErrorsCsv';

interface BulkUploadCsvDialogProps {
    open: boolean;
    csvContent: string;
    isImporting: boolean;
    importLog: ImportEvent[];
    importSummary: ImportSummary | null;
    onCsvChange: (value: string) => void;
    onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onStart: () => void;
    onClose: () => void;
}

export function BulkUploadCsvDialog({
    open,
    csvContent,
    isImporting,
    importLog,
    importSummary,
    onCsvChange,
    onFileChange,
    onStart,
    onClose,
}: BulkUploadCsvDialogProps) {
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>Bulk Upload Swift Flow Errors (CSV)</DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        Paste CSV content with headers:
                        {' '}
                        <strong>
                            Original Error Message, SwiftFlow Error Code, SwiftFlow Unified Message, Gateway, Notify, ErrorType
                        </strong>
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Button
                            variant="outlined"
                            size="small"
                            component="label"
                            startIcon={<UploadFile />}
                            disabled={isImporting}
                        >
                            Load from file
                            <input
                                type="file"
                                accept=".csv,text/csv"
                                hidden
                                onChange={onFileChange}
                            />
                        </Button>
                    </Box>

                    <TextField
                        label="CSV Content"
                        multiline
                        minRows={8}
                        fullWidth
                        value={csvContent}
                        onChange={(e) => onCsvChange(e.target.value)}
                        disabled={isImporting}
                        placeholder={
                            'Original Error Message,SwiftFlow Error Code,SwiftFlow Unified Message,Gateway,Notify,ErrorType\n' +
                            'Some message,ERR1001,User-friendly message,GATEWAY1,1,Validation'
                        }
                    />

                    {importSummary && (
                        <Box
                            sx={{
                                p: 1.5,
                                borderRadius: 1,
                                bgcolor: (theme) =>
                                    importSummary.errors > 0
                                        ? theme.palette.error.light + '33'
                                        : theme.palette.success.light + '33',
                                color: (theme) =>
                                    importSummary.errors > 0
                                        ? theme.palette.error.main
                                        : theme.palette.success.main,
                            }}
                        >
                            <Typography variant="body2">
                                {importSummary.totalRows} rows processed: {importSummary.created} created,{' '}
                                {importSummary.updated} updated, {importSummary.errors} errors.
                            </Typography>
                        </Box>
                    )}

                    <Box
                        sx={{
                            mt: 1,
                            maxHeight: 220,
                            overflowY: 'auto',
                            p: 1,
                            borderRadius: 1,
                            bgcolor: (theme) => theme.palette.background.default,
                            fontFamily: 'monospace',
                            fontSize: 12,
                        }}
                    >
                        {importLog.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                Import log will appear here as rows are processed.
                            </Typography>
                        ) : (
                            importLog.map((e, idx) => (
                                <Box key={`${e.row}-${e.code}-${idx}`} sx={{ mb: 0.5 }}>
                                    <Typography variant="body2">
                                        Row {e.row} – {e.code} – {e.action}
                                        {e.action === 'error' && e.error ? `: ${e.error}` : ''}
                                    </Typography>
                                </Box>
                            ))
                        )}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isImporting}>
                    Close
                </Button>
                <Button
                    onClick={onStart}
                    variant="contained"
                    disabled={isImporting}
                >
                    {isImporting ? 'Importing…' : 'Start Import'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
